import React, { ChangeEvent, useEffect, useState } from 'react';
import { InputAdornment, TextField } from '@material-ui/core';
import useFormatCurrency, { formatCurrencyNoIcon } from '../../localisation/useCurrency';
import CancelIcon from '@material-ui/icons/Cancel';

interface IFormInternationPrice {
    onChange: (value: number, maskedValue: string) => void;
    label: string;
    defaultValue?: string | number;
    decimalPlaces?: number;
    disabled?: boolean;
    hidden?: boolean;
    displaySymbol?: boolean;
    formProps?: any;
    keyName?: string;
}

export function FormInternationalPrice({
    onChange,
    label,
    defaultValue,
    disabled,
    hidden,
    decimalPlaces = 2,
    displaySymbol,
    formProps,
    keyName,
}: IFormInternationPrice) {
    const [maskedValue, setMaskedValue] = useState('0.00');
    const canDisplaySymbol = displaySymbol || (formProps?.displaySymbol ?? false);

    const { formatCurrency, currency } = useFormatCurrency();

    const adornments = {
        startAdornment: canDisplaySymbol ? (
            <InputAdornment
                position="start"
                style={{
                    borderRight: '1px solid #737fa4',
                    paddingRight: '1rem',
                    height: '1rem',
                }}
            >
                {currency.symbol}
            </InputAdornment>
        ) : null,
        endAdornment:
            !canDisplaySymbol && !disabled ? (
                <InputAdornment
                    onClick={() => {
                        !disabled && onChangeHandler(undefined, '0');
                    }}
                    position="start"
                >
                    <CancelIcon />
                </InputAdornment>
            ) : null,
    };

    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== null) {
            const { maskedValue } = updateValues(defaultValue);
            setMaskedValue(maskedValue);
        } else {
            const { maskedValue } = updateValues(0);
            setMaskedValue(maskedValue);
        }
        // eslint-disable-next-line
    }, [defaultValue]);
    function normaliseValues(value: string | number) {
        let returnValue = value;
        if (typeof value === 'string') {
            returnValue = cleanNumber(value);
            if (returnValue % 1 !== 0) {
                returnValue = returnValue.toFixed(2);
            }
        } else {
            if (!value) {
                return 0;
            }
            returnValue = Number.isInteger(value) ? Number(value) * 10 ** decimalPlaces : value.toFixed(2);
        }
        /** divide it by the power of the max fractions */
        return cleanNumber(returnValue) / 10 ** decimalPlaces;
    }
    function calculateValues(value: string | number) {
        const normalValue = normaliseValues(value);
        const formatFn = canDisplaySymbol ? formatCurrencyNoIcon : formatCurrency;
        const maskedValue = formatFn(normalValue, false);
        return {
            value: normalValue,
            maskedValue,
        };
    }

    function updateValues(updateValue: string | number) {
        const { value, maskedValue } = calculateValues(updateValue);
        if (value >= 0) {
            setMaskedValue(maskedValue);
            return {
                value,
                maskedValue,
            };
        } else {
            return { value: normaliseValues(maskedValue), maskedValue };
        }
    }
    function onChangeHandler(event?: ChangeEvent<HTMLInputElement>, overRideValue?: string) {
        if (event?.preventDefault) {
            event.preventDefault();
        }

        const { value, maskedValue } = updateValues(event?.target?.value || overRideValue!);
        if (maskedValue && onChange) {
            onChange(value, maskedValue);
        }
    }
    if (hidden) return null;
    return (
        <TextField
            style={{ padding: '5px' }}
            label={label}
            onChange={(ev) => onChangeHandler(ev as ChangeEvent<HTMLInputElement>)}
            value={maskedValue}
            InputProps={adornments}
            disabled={disabled}
            name={keyName ?? 'int_pricing_option'}
            data-testid="form-int-pricing"
            data-component-name={'OPTION_PRICE'}
            variant={'outlined'}
        />
    );
}

function cleanNumber(value: number | string) {
    if (typeof value === 'number') {
        return value;
    }
    return Number(value.toString().replace(/[^0-9-]/g, ''));
}
